
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface BookingChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingChoiceModal({ isOpen, onClose }: BookingChoiceModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-[fadeIn_0.2s_ease-out]">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#24820C] to-[#83B328] p-6 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            How Would You Like to Book?
          </h2>
          <p className="text-white/90 text-sm md:text-base">
            Choose the option that works best for you
          </p>
        </div>

        {/* Options */}
        <div className="p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Option 1 - In-Person Evaluation */}
            <Link
              to="/qualified-booking"
              onClick={onClose}
              className="group relative bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-[#24820C] rounded-xl p-6 transition-all cursor-pointer block"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-[#24820C]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#24820C]/20 transition-colors">
                  <i className="ri-user-location-line text-3xl text-[#24820C]"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  In-Person Evaluation
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  Schedule a free on-site consultation with our expert team for a personalized assessment
                </p>
                <div className="flex items-center justify-center text-sm text-gray-500">
                  <i className="ri-calendar-line mr-1"></i>
                  <span>Takes 1-2 business days</span>
                </div>
              </div>
            </Link>

            {/* Option 2 - Instant Service (Most Popular) */}
            <a
              href="https://book.jslandscapingmd.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="group relative bg-[#83B328]/5 hover:bg-[#83B328]/10 border-2 border-[#83B328] hover:border-[#6d9620] rounded-xl p-6 transition-all cursor-pointer block ring-2 ring-[#83B328]/20"
            >
              {/* Most Popular Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#83B328] text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg whitespace-nowrap">
                  ⭐ MOST POPULAR
                </span>
              </div>

              <div className="text-center mt-2">
                <div className="w-16 h-16 bg-[#83B328]/15 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#83B328]/25 transition-colors">
                  <i className="ri-flashlight-line text-3xl text-[#83B328]"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Book Instant Service
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  Get an instant quote and book your service right away, no waiting required
                </p>
                <div className="flex items-center justify-center text-sm text-[#83B328] font-semibold">
                  <i className="ri-lightning-line mr-1"></i>
                  <span>Instant booking available</span>
                </div>
              </div>
            </a>
          </div>

          {/* Social Proof */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              <i className="ri-group-line mr-1"></i>
              <strong className="text-gray-700">87% of customers</strong> choose instant service for faster results
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
